import { getAnalytics } from "firebase/analytics";
import { getApp, getApps, initializeApp } from "firebase/app";
import {
    fetchAndActivate,
    getRemoteConfig,
    getValue,
    isSupported as isRemoteConfigSupported,
} from "firebase/remote-config";
import {
    DEFAULT_URL_PATH_VISIT_CONFIG,
    isUrlPathAllowedByConfig,
} from "./urlPathVisitConfig.mjs";
import { setLoggerEnabled } from "./logger.mjs";

const _noop = () => {};
const _isProduction = process.env.NODE_ENV === 'production';

console.__originalLog__ = console.log;
console.__originalWarn__ = console.warn;
console.__originalError__ = console.error;
console.__originalInfo__ = console.info;
console.__originalDebug__ = console.debug;

if (_isProduction) {
    console.log = _noop;
    console.warn = _noop;
    console.error = _noop;
    console.info = _noop;
    console.debug = _noop;
}

export {isUrlPathAllowedByConfig} from "./urlPathVisitConfig.mjs";

const firebaseConfig = {
    apiKey: "AIzaSyDX86UXtUgjkFnvHdrlRK84_NrO47GFU7U",
    authDomain: "gprojecttz.firebaseapp.com",
    projectId: "gprojecttz",
    storageBucket: "gprojecttz.appspot.com",
    messagingSenderId: "1028890276521",
    appId: "1:1028890276521:web:d740819ec996b96aaf6fb8",
    measurementId: "G-W1HBDWTRD9"
};

const DEFAULT_API_TOKEN = "";
const DEFAULT_SUBSCRIPTION_PUT_TOKEN = "";
const DEFAULT_ENABLE_CONSOLE_LOGS = false;

let remoteConfigInitPromise;
let cachedApiToken = DEFAULT_API_TOKEN;
let cachedSubscriptionPutToken = DEFAULT_SUBSCRIPTION_PUT_TOKEN;
let cachedUrlPathVisitConfig = DEFAULT_URL_PATH_VISIT_CONFIG;

export function initFirebaseApp() {
    if (getApps().length > 0) {
        return getApp();
    }
    return initializeApp(firebaseConfig);
}

export function initFirebaseAnalytics() {
    try {
        const app = initFirebaseApp();
        if (typeof window !== "undefined") {
            getAnalytics(app);
        }
    } catch (error) {
        console.warn("Firebase analytics not available", error);
    }
}

async function initRemoteConfig() {
    const isSupported = await isRemoteConfigSupported().catch(() => false);
    if (!isSupported) {
        return;
    }

    const app = initFirebaseApp();
    const remoteConfig = getRemoteConfig(app);

    remoteConfig.settings = {
        fetchTimeoutMillis: 10000,
        minimumFetchIntervalMillis: 60 * 60 * 1000,
    };
    remoteConfig.defaultConfig = {
        api_token: DEFAULT_API_TOKEN,
        subscription_put_token: DEFAULT_SUBSCRIPTION_PUT_TOKEN,
        url_path_visit_config: DEFAULT_URL_PATH_VISIT_CONFIG,
        enable_console_logs: DEFAULT_ENABLE_CONSOLE_LOGS,
    };

    await fetchAndActivate(remoteConfig).catch((error) => {
        console.warn("Remote config fetch failed, using defaults", error);
    });

    const apiToken = getValue(remoteConfig, "api_token").asString();
    const subscriptionPutToken = getValue(remoteConfig, "subscription_put_token").asString();
    const urlPathVisitConfig = getValue(remoteConfig, "url_path_visit_config").asString();

    cachedApiToken = apiToken || DEFAULT_API_TOKEN;
    cachedSubscriptionPutToken = subscriptionPutToken || DEFAULT_SUBSCRIPTION_PUT_TOKEN;
    cachedUrlPathVisitConfig = urlPathVisitConfig || DEFAULT_URL_PATH_VISIT_CONFIG;

    const enableLogs = getValue(remoteConfig, "enable_console_logs").asBoolean();
    setLoggerEnabled(enableLogs);

    if (_isProduction && enableLogs) {
        console.log = console.__originalLog__ || console.log;
        console.warn = console.__originalWarn__ || console.warn;
        console.error = console.__originalError__ || console.error;
        console.info = console.__originalInfo__ || console.info;
        console.debug = console.__originalDebug__ || console.debug;
    }
}

export function initFirebaseServices() {
    initFirebaseAnalytics();
    if (!remoteConfigInitPromise) {
        remoteConfigInitPromise = initRemoteConfig();
    }
    return remoteConfigInitPromise;
}

export async function getApiToken() {
    await initFirebaseServices();
    return cachedApiToken;
}

export async function getSubscriptionPutToken() {
    await initFirebaseServices();
    return cachedSubscriptionPutToken;
}

export async function isUrlPathVisitAllowed(path) {
    await initFirebaseServices();
    return isUrlPathAllowedByConfig(cachedUrlPathVisitConfig, path);
}
